import _ from 'lodash';

/**
 * Overlay nodes onto the page
 * @see ../service/AnnouncementService.md for usage
 * @param {Array} nodesToOverlay  Each should be an Element
 * @return {{setup: Function, teardown: Function}}
 */
export function overlay(nodesToOverlay) {
	// create a positioning function that can be throttled
	const run = () => positionAll(nodesToOverlay);
	// throttle that function
	const runThrottled = _.throttle(run, 100, { leading: false, trailing: true });
	// define a function to initially position the nodes
	const setup = () => {
		window.addEventListener('resize', runThrottled);
		run();
	};
	// define a function to remove the nodes
	const teardown = () => {
		[...nodesToOverlay].forEach(removeNode);
		window.removeEventListener('resize', runThrottled);
	};
	// return those setup and teardown functions
	return { setup, teardown };
}

/**
 * Position all the given nodes
 * @param {Element[]} nodeList
 */
function positionAll(nodeList) {
	[...nodeList].forEach(positionNode);
}

/**
 * Position the given node
 * @param {Element} node
 */
function positionNode(node) {
	// find the node that we should position to
	const destSelector = node.getAttribute('data-dest-node');
	const dest = document.querySelector(destSelector);
	if (!dest) {
		node.style.display = 'none';
		return;
	}
	// get the corner keywords
	const corner = node.getAttribute('data-dest-corner') || 'top-left';
	// get the offsets we should obey
	const left = parseFloat(node.getAttribute('data-dest-left')) || 0;
	const top = parseFloat(node.getAttribute('data-dest-top')) || 0;
	// get the current position of the destination node
	const rect = dest.getBoundingClientRect();
	// calculate the offsets
	const offset = getOffset(rect, corner, left, top);
	// apply the offsets
	Object.assign(node.style, {
		position: 'absolute',
		left: `${offset.left}px`,
		top: `${offset.top}px`,
		display: '',
	});
	// position relative to body
	document.body.appendChild(node);
}

/**
 * Get the pixel offset based on the corner keywords and offset
 * @param {ClientRect} rect  The rect object from getBoundingClientRect()
 * @param corner  The corner keywords. one of the following:
 *     top-left     top-center     top-right
 *     middle-left  middle-center  middle-right
 *     bottom-left  bottom-center  bottom-right
 * Note that it is ok for the keywords to be backwards (e.g. "center-middle")
 * or to have another character separator (e.g. "bottom right")
 * @param left  The number of pixels to add to the left offset
 * @param top  The number of pixels to add to the top offset
 * @return {{top: Number, left: Number}}
 */
function getOffset(rect, corner, left, top) {
	let x = rect.left + left;
	let y = rect.top + top;
	// if we are top-left we're good to go.
	// But if we have right, bottom, center, and middle, we need to add
	if (corner.indexOf('right') > -1) {
		x += rect.width;
	}
	if (corner.indexOf('bottom') > -1) {
		y += rect.height;
	}
	if (corner.indexOf('center') > -1) {
		x += rect.width / 2;
	}
	if (corner.indexOf('middle') > -1) {
		y += rect.height / 2;
	}
	return {
		left: Math.round(x),
		top: Math.round(y),
	};
}

/**
 * Attempt to remove the given node
 * @param {Element} node
 */
function removeNode(node) {
	try {
		node.parentNode.removeChild(node);
	} catch (e) {}
}
